import { useState, useEffect } from 'react';
import { Icon, colorVar } from '../components/Icons';
import { AnimatedNumber } from '../components/AnimatedNumber';
import { useIsMobile } from '../hooks/useIsMobile';

const LOG_COLORS = {
  'deep-sage': 'var(--deep-sage)',
  'periwinkle': 'var(--periwinkle)',
  'crimson': 'var(--crimson)',
  'deep-rose': 'var(--deep-rose)',
  'deep-gold': 'var(--deep-gold)',
  'sage': 'var(--sage)',
  'rose': 'var(--rose)',
  'gold': 'var(--gold)',
  'ink': 'var(--ink)',
  'blush': 'var(--blush)',
};

const DEFAULT_HERO_TAGS = [
  'Short Form Strategy','Brand Voice','Social Operations','AI Integration',
  'SOPs & Systems','On-Camera Talent','Team Training','Analytics','Platform Strategy','Vertical Video',
];

// Homepage "What I Do" services block — two tracks (brands / employers).
function ServicesSection({ services, email, isMobile }) {
  if (!services || !Array.isArray(services.tracks) || services.tracks.length === 0) return null;
  return (
    <div style={{ marginTop: 16 }}>
      <div className="panel">
        <div className="panel-header">
          <span className="panel-title">{services.label || 'What I Do'}</span>
        </div>
        <div style={{ padding: '4px 18px 20px' }}>
          {services.intro && (
            <p style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.6, margin: '6px 0 18px' }}>{services.intro}</p>
          )}
          {services.tracks.map((track, ti) => (
            <div key={track.id || ti} style={{ marginBottom: ti < services.tracks.length - 1 ? 22 : 10 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 800, fontStyle: 'italic', color: 'var(--ink)' }}>{track.heading}</span>
                {track.sub && <span style={{ fontFamily: "'Courier Prime', monospace", fontSize: 11, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text3)' }}>{track.sub}</span>}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : `repeat(${Math.min((track.items || []).length, 3)}, 1fr)`, gap: 12 }}>
                {(track.items || []).map((s, i) => (
                  <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 14, padding: '14px 14px 16px', background: 'rgba(255,255,255,.5)' }}>
                    <div style={{ fontSize: 14.5, fontWeight: 800, color: 'var(--ink)', marginBottom: 8 }}>{s.title}</div>
                    {s.tag && <span className={`chip chip-${s.color || 'rose'}`} style={{ display: 'inline-block', marginBottom: 8 }}>{s.tag}</span>}
                    <p style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.55, margin: '4px 0 0' }}>{s.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {(services.ctaLabel || services.ctaButton) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
              {services.ctaLabel && <span style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic', fontWeight: 700, color: 'var(--ink)' }}>{services.ctaLabel}</span>}
              <a href={email ? `mailto:${email}` : '#'} style={{ fontSize: 13, fontWeight: 800, padding: '7px 16px', borderRadius: 20, background: 'var(--crimson)', color: 'white', textDecoration: 'none' }}>{services.ctaButton || "Let's Talk"} →</a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const DEFAULT_ACTIVITY_LOG = [
  { time: 'NOW', color: 'deep-sage', text: "Open to Director-level brand & social roles" },
  { time: '2026', color: 'periwinkle', text: "12.7M views a month · 2.9M a week · sustained all year" },
  { time: '2026', color: 'crimson', text: "#1 on my team in IG & TikTok · 55% of team output" },
  { time: '2025', color: 'deep-rose', text: "AI workflow cuts editing time by 80%" },
  { time: '2025', color: 'deep-gold', text: "API automation. Content in under 10 seconds" },
  { time: '2024', color: 'sage', text: "24/7 streaming launched, KUSA + WXIA" },
  { time: '2023', color: 'deep-gold', text: "Regional Murrow Award winner" },
  { time: '2022', color: 'periwinkle', text: "2× Southeast Emmy nominated" },
];

// Large homepage portrait. Single photo now, auto-scrolling gallery if profile.photos has several.
function PhotoGallery({ photos, photoMeta, name }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (photos.length <= 1) return;
    const t = setInterval(() => setIdx(i => (i + 1) % photos.length), 3500);
    return () => clearInterval(t);
  }, [photos.length]);

  if (photos.length === 0) {
    return (
      <div style={{ width: '100%', height: '100%', minHeight: 260, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, background: 'rgba(255,255,255,.05)', border: '2px dashed rgba(255,255,255,.22)', borderRadius: 18 }}>
        <Icon name="camera" size={44} stroke="rgba(255,255,255,.4)" strokeWidth={1.5} />
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', minHeight: 280, borderRadius: 18, overflow: 'hidden', boxShadow: '0 8px 30px rgba(0,0,0,.25)' }}>
      {photos.map((src, i) => {
        const meta = (photoMeta && photoMeta[i]) || {};
        const zoom = meta.zoom ?? 1;
        const x = meta.x ?? 50;
        const y = meta.y ?? 0;
        return (
          <img key={i} src={src} alt={name}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: `${x}% ${y}%`, transform: `scale(${zoom})`, transformOrigin: `${x}% ${y}%`, opacity: i === idx ? 1 : 0, transition: 'opacity .8s ease' }} />
        );
      })}
      {photos.length > 1 && (
        <div style={{ position: 'absolute', bottom: 10, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 6, zIndex: 2 }}>
          {photos.map((_, i) => (
            <span key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: i === idx ? 'white' : 'rgba(255,255,255,.45)', transition: 'background .3s' }} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function Overview({ content }) {
  const isMobile = useIsMobile();
  const profile = content?.profile || {};
  const stats = content?.stats || [];
  const pills = content?.pills || [];
  const heroTags = profile.heroTags || content?.heroTags || DEFAULT_HERO_TAGS;
  const activityLog = content?.activityLog || DEFAULT_ACTIVITY_LOG;
  const photos = (Array.isArray(profile.photos) && profile.photos.length)
    ? profile.photos
    : (profile.photo ? [profile.photo] : []);
  const photoMeta = profile.photoMeta || [];

  // Stat grid: on mobile show 2 columns
  const statCols = isMobile
    ? 'repeat(2, 1fr)'
    : `repeat(${stats.length}, 1fr)`;

  return (
    <div className="page-enter">
      {/* Topbar */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: isMobile ? 26 : 32, fontWeight: 900, fontStyle: 'italic', color: 'var(--ink)', lineHeight: 1.1, letterSpacing: '-.02em' }}>
          Hi. I'm <span style={{ color: 'var(--deep-rose)' }}>{profile.name?.split(' ')[0] || 'Makayla'} {profile.name?.split(' ')[1] || 'Richards'}.</span>
        </div>
        <div style={{ fontFamily: "'Courier Prime', monospace", fontSize: 13, color: 'var(--text3)', marginTop: 6, letterSpacing: '.04em' }}>
          // {profile.subtitle || 'Social & Brand Strategist · Atlanta, GA'}
        </div>
      </div>

      {/* Pills */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 18 }}>
        {pills.map(p => (
          <span key={p.id} style={{ fontSize: 13, fontWeight: 700, padding: '4px 12px', borderRadius: 20, border: '1.5px solid', letterSpacing: '.03em', color: colorVar(p.color, 'text'), borderColor: colorVar(p.color, 'border'), background: colorVar(p.color, 'bg') }}>
            {p.text}
          </span>
        ))}
      </div>

      {/* Stat row */}
      <div style={{ display: 'grid', gridTemplateColumns: statCols, gap: 12, marginBottom: 16 }}>
        {stats.map((s, i) => (
          <div key={s.id} className="panel" style={{ padding: '16px 14px', position: 'relative', overflow: 'hidden', cursor: 'default', animationDelay: `${i * 0.05}s` }}>
            <div style={{ position: 'absolute', top: -12, right: -12, width: 54, height: 54, borderRadius: '50%', background: colorVar(s.color, 'deep'), opacity: .1 }} />
            <div style={{ width: 32, height: 32, marginBottom: 8 }}>
              <Icon name={s.icon} size={32} stroke={colorVar(s.color, 'deep')} />
            </div>
            <div style={{ fontFamily: "'Courier Prime', monospace", fontSize: 11, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--text3)', marginBottom: 4 }}>{s.label}</div>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: isMobile ? 22 : 28, fontWeight: 900, lineHeight: 1, marginBottom: 3, color: colorVar(s.color, 'deep') }}>
              <AnimatedNumber value={s.value} />
            </div>
            <div style={{ fontSize: 12, color: 'var(--text3)', fontWeight: 600 }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Hero + Log */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.15fr 1fr', gap: 14 }}>
        {/* Hero */}
        <div style={{ background: 'linear-gradient(135deg, var(--ink) 0%, var(--plum) 55%, #3d2247 100%)', borderRadius: 18, color: 'white', position: 'relative', overflow: 'hidden', boxShadow: 'var(--shadow-hover)', display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 4 }}>
          <div style={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(247,207,214,.07)' }} />
          {/* Photo — large, gallery-ready */}
          <div style={{ width: isMobile ? '100%' : 240, minWidth: isMobile ? 'auto' : 240, padding: isMobile ? '16px 16px 0' : 16, position: 'relative', zIndex: 1 }}>
            <div style={{ height: isMobile ? 280 : '100%', overflow: 'hidden', borderRadius: 18 }}>
              <PhotoGallery photos={photos} photoMeta={photoMeta} name={profile.name} />
            </div>
          </div>
          {/* Bio */}
          <div style={{ flex: 1, padding: isMobile ? '16px 22px 22px' : '22px 22px 22px 8px', position: 'relative', zIndex: 1 }}>
            <div style={{ fontFamily: "'Courier Prime', monospace", fontSize: 12, letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--rose)', marginBottom: 9 }}>// The full picture</div>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: isMobile ? 22 : 26, fontWeight: 900, fontStyle: 'italic', lineHeight: 1.15, marginBottom: 8 }}>
              {(profile.tagline || 'Not just a producer.').replace(/\s*An operator\.?\s*$/i, '').trim() || 'Not just a producer.'}<br />
              <span style={{ color: 'var(--rose)' }}>{profile.taglineAccent || 'An operator.'}</span>
            </div>
            <p style={{ fontSize: 14, color: 'rgba(255,255,255,.88)', lineHeight: 1.7, marginBottom: 14 }}>{profile.bio}</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {heroTags.map(s => (
                <span key={s} style={{ fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: 'rgba(255,255,255,.12)', color: 'rgba(255,255,255,.90)', border: '1px solid rgba(255,255,255,.18)', cursor: 'default' }}>{s}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Activity log */}
        <div className="panel">
          <div className="panel-header">
            <span className="panel-title">Activity Log</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontFamily: "'Courier Prime', monospace", fontSize: 10, color: 'var(--deep-sage)' }}>
              <span className="status-dot" style={{ background: 'var(--deep-sage)' }} /> LIVE
            </span>
          </div>
          <div style={{ padding: '6px 18px 10px' }}>
            {activityLog.map((item, i) => (
              <div key={i} style={{ display: 'flex', gap: 9, alignItems: 'flex-start', padding: '8px 0', borderBottom: i < activityLog.length - 1 ? '1px solid var(--border)' : 'none', fontSize: 14, color: 'var(--text2)' }}>
                <span style={{ fontFamily: "'Courier Prime', monospace", fontSize: 12, color: 'var(--text3)', minWidth: 42, paddingTop: 1 }}>{item.time}</span>
                <span style={{ width: 7, height: 7, borderRadius: '50%', marginTop: 5, flexShrink: 0, background: LOG_COLORS[item.color] || item.color }} />
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Services — What I Do */}
      <ServicesSection services={content?.services} email={profile.email} isMobile={isMobile} />
    </div>
  );
}
